<template>
  <div class="automations-modal" role="dialog" aria-modal="true" aria-labelledby="automations-duplicate-title">
    <div class="automations-modal__backdrop" @click="close"></div>

    <div class="automations-modal__shell">
      <header class="automations-modal__header">
        <div class="min-w-0">
          <h2 id="automations-duplicate-title" class="automations-modal__title">
            Duplicate {{ automations.length }} {{ automations.length === 1 ? 'automation' : 'automations' }}
          </h2>
          <p class="automations-modal__subtitle">
            Copies are created as <strong>drafts</strong>, so they won't run until you activate them.
          </p>
        </div>
        <button type="button" class="automations-modal__close" aria-label="Close" :disabled="saving" @click="close">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </header>

      <div class="automations-modal__body">
        <div v-if="loading" class="text-center p-m">
          <spinner></spinner>
        </div>
        <template v-else>
          <section class="m-b-md">
            <label for="automations-duplicate-question" class="automations-modal__label">Write to property</label>
            <select id="automations-duplicate-question" v-model="targetQuestionId" class="form-control">
              <option :value="null">Keep current property</option>
              <option v-for="q in questions" :key="`q-${q.id}`" :value="q.id">{{ q.prompt }}</option>
            </select>
            <p class="automations-modal__hint">
              Only private, single select wrestler properties are listed. Descriptions mentioning the old property are updated to the new one.
            </p>
            <div v-if="targetQuestion && sourceQuestionCount > 1" class="automations-modal__warning" role="alert">
              The selected automations write to {{ sourceQuestionCount }} different properties. Every copy will write to <strong>{{ targetQuestion.prompt }}</strong>.
            </div>
            <div v-if="missingAnswers.length > 0" class="automations-modal__warning" role="alert">
              <strong>{{ targetQuestion.prompt }}</strong> is missing
              {{ missingAnswers.length === 1 ? 'the option' : 'these options' }}:
              {{ missingAnswers.join(', ') }}. Add {{ missingAnswers.length === 1 ? 'it' : 'them' }} to the property before duplicating.
            </div>
          </section>

          <section>
            <label class="automations-modal__label">Replace tags</label>
            <p v-if="tagRows.length === 0" class="automations-modal__hint">
              The selected automations don't count any roster tags.
            </p>
            <template v-else>
              <p class="automations-modal__hint">
                We guessed next season's tag where one exists. Create any missing tags first, and pick "no change" to keep a tag as is.
              </p>
              <table class="table automations-tag-table m-b-none">
                <thead>
                  <tr>
                    <th>Current tag</th>
                    <th>Replace with</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in tagRows" :key="`tag-row-${row.from}`">
                    <td class="font-semi text-gray-900">{{ row.from }}</td>
                    <td>
                      <select v-model="row.to" class="form-control input-sm" :aria-label="`Replacement for ${row.from}`">
                        <option :value="row.from">{{ row.from }} (no change)</option>
                        <option v-for="tag in tagOptionsFor(row.from)" :key="`tag-opt-${row.from}-${tag.id}`" :value="tag.abbreviation">
                          {{ tag.abbreviation }} &mdash; {{ tag.name }}
                        </option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
          </section>
        </template>

        <div v-if="errors.length > 0" class="alert alert-danger m-t-m m-b-none" role="alert">
          <p class="font-semi m-b-xxs">We couldn't duplicate these automations:</p>
          <ul class="m-b-none p-l-m">
            <li v-for="(msg, i) in errors" :key="`dup-err-${i}`">{{ msg }}</li>
          </ul>
        </div>
      </div>

      <footer class="automations-modal__footer">
        <button type="button" class="btn btn-default" :disabled="saving" @click="close">Cancel</button>
        <button type="button" class="btn btn-primary" :disabled="saving || loading" @click="submit">
          <spinner v-if="saving" :inline="true"></spinner>
          Create drafts
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import Spinner from './spinner.vue';
import { errorableMix } from './mixins/errorable_mix';
import { guessNextSeasonTag } from '../utilities/season_tags';

export default {
  name: 'automations-duplicate-modal',
  components: { Spinner },
  mixins: [errorableMix],
  props: {
    // The selected automation objects from the index endpoint
    automations: { type: Array, required: true },
  },
  data() {
    return {
      loading: true,
      saving: false,
      questions: [],
      tags: [],
      targetQuestionId: null,
      // [{ from: "S2526", to: "S2627" }]
      tagRows: [],
      errors: [],
    };
  },
  computed: {
    targetQuestion() {
      if (!this.targetQuestionId) return null;
      return this.questions.find((q) => q.id === this.targetQuestionId) || null;
    },
    // Answer values the selected automations write that the chosen property can't hold
    missingAnswers() {
      if (!this.targetQuestion) return [];
      const answers = this.targetQuestion.answers || [];
      const values = _.uniq(this.automations.map((a) => a.action_data && a.action_data.action_registration_answer_value));
      return values.filter((v) => v && !answers.includes(v));
    },
    // Distinct properties the selected automations currently write to
    sourceQuestionCount() {
      return _.uniq(this.automations.map((a) => a.action_data && String(a.action_data.action_registration_question_id))).length;
    },
    // Live team tags with an abbreviation, one per abbreviation (case-insensitive), sorted
    liveTags() {
      const usable = this.tags.filter((t) => !t.archived && t.abbreviation);
      const unique = _.uniqBy(usable, (t) => t.abbreviation.toLowerCase());
      return _.sortBy(unique, (t) => t.abbreviation.toLowerCase());
    },
  },
  created() {
    this.load();
    document.body.classList.add('composer-overlay-open');
    document.addEventListener('keydown', this.handleKeydown);
  },
  destroyed() {
    document.body.classList.remove('composer-overlay-open');
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    load() {
      const vm = this;
      const questionsUrl = `${vm.$apiService.registrationQuestionsUrl()}?visibility=private&for_type=wrestlers&per_page=100`;
      const tagsUrl = `${vm.$apiService.tagsUrl()}?per_page=200`;

      Promise.all([
        vm.$apiService.loadAllPages(questionsUrl, 'registration_questions', []),
        vm.$apiService.loadAllPages(tagsUrl, 'tags', []),
      ])
        .then(([questions, tags]) => {
          vm.questions = questions.filter((q) => q.type === 'RegSingleSelectQuestion');
          vm.tags = tags;
          vm.seedTagRows();
          vm.loading = false;
        })
        .catch(() => {
          vm.loading = false;
          vm.errors = ['Unable to load properties and tags. Please refresh and try again.'];
        });
    },
    seedTagRows() {
      const abbreviations = _.uniq(_.flatMap(this.automations, (a) => a.tag_abbreviations || []));
      this.tagRows = abbreviations.sort().map((from) => ({ from, to: this.guessReplacement(from) }));
    },
    // Only pre-fill a guess that matches a real tag; otherwise leave the tag unchanged
    guessReplacement(abbreviation) {
      return guessNextSeasonTag(abbreviation, this.liveTags.map((t) => t.abbreviation));
    },
    // The row's own abbreviation is already offered as "no change"
    tagOptionsFor(from) {
      return this.liveTags.filter((t) => t.abbreviation.toLowerCase() !== from.toLowerCase());
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }
    },
    close() {
      if (this.saving) return;
      this.$emit('close');
    },
    submit() {
      const vm = this;
      if (vm.saving) return;
      vm.errors = [];
      vm.saving = true;

      // Only send tags that actually change; unmapped tags keep their abbreviation server side
      const tagMap = {};
      vm.tagRows.forEach((row) => {
        if (row.to && row.to !== row.from) tagMap[row.from] = row.to;
      });

      const params = {
        automation_ids: vm.automations.map((a) => a.id),
        tag_map: tagMap,
      };
      if (vm.targetQuestionId) params.registration_question_id = vm.targetQuestionId;

      axios.post(`${vm.$apiService.automationsUrl()}/duplicate`, params)
        .then((response) => {
          vm.saving = false;
          vm.$emit('created', response.data.automations || []);
        })
        .catch((error) => {
          vm.saving = false;
          const errors = error.response && error.response.data && error.response.data.errors;
          if (Array.isArray(errors)) {
            vm.errors = errors;
          } else {
            vm.errors = [error.response ? vm.parseErrorResponse(error) : 'Something went wrong. Please try again.'];
          }
        });
    },
  },
};
</script>
